/**
 * Computer sound — is the computer's sound on, and is it playing right now.
 *
 * Sound belongs to the computer, not to the pane that shows it. Once the person
 * presses Unmute, the sound keeps playing when they switch the computer's tab,
 * close the panel, or go to another page in the app, the way a computer keeps
 * playing music behind other windows. One app-level player
 * (ComputerSoundPlayer, mounted in AppShell) plays it; the pane's own <video>
 * stays muted, so there is never a second copy of the sound and no hand-off
 * when the pane goes away.
 *
 *  - `on`: the person turned the sound on and has not turned it off. Mute, the
 *    pill's Stop, the computer's sleep and the stream ending all turn it off.
 *  - `audible`: the player heard sound within its silence window (it meters the
 *    stream). Sound on but silent (the media ended or was paused) is not
 *    playing: no pill, and no keep-awake beat.
 *  - `panes`: how many browser panes are on screen. The pill says "Playing from
 *    your computer" only while none is, because an open pane carries its own
 *    Mute button.
 *  - `notice`: the sound stopped by itself, after an hour without the person
 *    doing anything in the app ("idle") or at the three-hour cap on the box's
 *    keep-awake hold ("cap"); the pill's place says so for a few seconds.
 */
import { create } from "zustand";

interface ComputerSoundState {
  on: boolean;
  /** The task whose Computer the sound was turned on from; the pill reopens it. */
  taskId: string | null;
  audible: boolean;
  panes: number;
  notice: "idle" | "cap" | null;
  turnOn: (taskId: string | null) => void;
  turnOff: () => void;
  /** The hour without activity ran out: off, as Stop does, and say so. */
  stopAfterIdle: () => void;
  /** The box's three-hour hold is about to end: off, as Stop does, and say so. */
  stopAfterCap: () => void;
  clearNotice: () => void;
  setAudible: (audible: boolean) => void;
  /** A browser pane came on screen. Returns its release, for an effect cleanup. */
  paneShown: () => () => void;
}

export const useComputerSoundStore = create<ComputerSoundState>((set) => ({
  on: false,
  taskId: null,
  audible: false,
  panes: 0,
  notice: null,
  turnOn: (taskId) => set({ on: true, taskId, notice: null }),
  turnOff: () => set({ on: false, audible: false }),
  stopAfterIdle: () => set({ on: false, audible: false, notice: "idle" }),
  stopAfterCap: () => set({ on: false, audible: false, notice: "cap" }),
  clearNotice: () => set({ notice: null }),
  setAudible: (audible) => set({ audible }),
  paneShown: () => {
    set((s) => ({ panes: s.panes + 1 }));
    return () => set((s) => ({ panes: s.panes - 1 }));
  },
}));

/** Sound on, audible, and no pane showing it: the pill's one condition. */
export function isPlayingInBackground(s: {
  on: boolean;
  audible: boolean;
  panes: number;
}): boolean {
  return s.on && s.audible && s.panes === 0;
}
